import React, { useEffect, useRef } from "react";
import { Button, Container, Group, Loader, Paper, ScrollArea, Select, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconChevronLeft } from "@tabler/icons-react";

import { useGetAzureADUsersQuery, useGetAzureAppRolesQuery } from "../../api/azuread.api";
import { MutableUserDto, UserDto } from "../../models/user.models";
import { RoleTemplate } from "../RoleTemplate";
import { AzureADRoles } from "../../Auth/auth-roles";
import classes from './UserDetailView.module.css';

interface UserEditViewProps {
	user?: UserDto | null;
	onSave: (values: MutableUserDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	// isChanged?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const UserEditView: React.FC<UserEditViewProps> = ({
	user,
	onSave,
	onClose,
	isSaving = false,
	// isChanged = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);

	const { data: azureAdUsers, isPending: isLoadingAzureAdUsers } = useGetAzureADUsersQuery();
	const azureAdUserItems = azureAdUsers?.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName)).map(user => ({ value: user.Id, label: `${user.DisplayName} (${user.Email})` })) || [];

	const { data: azureAppRoles, isPending: isLoadingAzureAppRoles } = useGetAzureAppRolesQuery();
	const azureAppRoleItems = azureAppRoles?.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName)).map(role => ({ value: role.Id, label: role.DisplayName })) || [];

	const isNew = !user || user.UserId === "new";

	const form = useForm<MutableUserDto>({
		initialValues: {
			UserId: user?.UserId || "",
			AzurePrincipalId: user?.AzurePrincipalId || "",
			AzureAppRoleId: user?.AzureAppRoleId || ""
		},
		validate: {
			AzurePrincipalId: (value) => (!value || value === "" ? "User is required" : null),
			AzureAppRoleId: (value) => (!value || value === "" ? "Role is required" : null)
		},
	});

	// Track dirty state and call onChanged
	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty()) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		form.setValues({
			UserId: user?.UserId || "",
			AzurePrincipalId: user?.AzurePrincipalId || "",
			AzureAppRoleId: user?.AzureAppRoleId || "",
		});
		// eslint-disable-next-line
	}, [user]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};

	const noAccess = (
		<Paper withBorder p="md" radius="md">
			<Text>You do not have permission to create or edit users. Please contact your administrator.</Text>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{isNew ? "New User" : "Edit User"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20" mb="lg">
					<form onSubmit={form.onSubmit((values) => handleSave(values))}>
						<Button type="submit" style={{ display: 'none' }} />
						<Select
							label="Select a user from the directory"
							key={form.key("AzurePrincipalId")}
							{...form.getInputProps("AzurePrincipalId")}
							value={form.values.AzurePrincipalId.toString()}
							placeholder={isLoadingAzureAdUsers ? "Loading..." : "Search for a user"}
							data={azureAdUserItems}
							leftSection={isLoadingAzureAdUsers ? <Loader size={16} /> : null}
							disabled={!isNew}
							required
							searchable
							mb="xl"
						/>

						<Select
							label="Select a role to assign"
							key={form.key("AzureAppRoleId")}
							{...form.getInputProps("AzureAppRoleId")}
							value={form.values.AzureAppRoleId}
							placeholder={isLoadingAzureAppRoles ? "Loading..." : "Select role"}
							data={azureAppRoleItems}
							leftSection={isLoadingAzureAppRoles ? <Loader size={16} /> : null}
							required
							mb="xl"
						/>
					</form>

					<Text fz="sm" fw="500" mb="5">Role description:</Text>
					<Text fz="sm" component="div"><div dangerouslySetInnerHTML={{ __html: azureAppRoles?.find(role => role.Id === form.values.AzureAppRoleId)?.Description.replace(/\n/g, "<br />") || "(select a role first)" }} /></Text>

					{/* <pre>{JSON.stringify(form.values, null, 2)}</pre> */}
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>
};

export default UserEditView;